// Import Capability From Other Role: the manager picks capabilities from the
// other roles of the same team, and this role then shares each one with the
// role it came from: one capability (one id), with the same name, the same
// levels and the same grading for both (see ./sharedCapabilities.ts).
// Capabilities are shared within a team only, so other teams' roles are not offered.

import React, { useMemo, useState } from 'react';
import { Box, Checkbox, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MAX_CAPABILITIES, type ImportChoice, type SetupCapability, type SetupRole } from './capabilitySetup';
import {
  ActionButton,
  BODY,
  BRANDY,
  DANGER,
  FONT,
  LABEL,
  LINE,
  Note,
  SECTION_TITLE,
  TEXT,
  TEXT_2,
  TEXT_3
} from './designSystem';
import { importable, sameName } from './sharedCapabilities';
import { LEVEL_ORDER } from '../Program/components/learning-lifecycle/teamCapability';

const levelsText = (capability: SetupCapability) => {
  const written = LEVEL_ORDER.filter((level) => (capability.criteria[level] ?? '').trim()).length;
  if (written === LEVEL_ORDER.length) return 'All 4 levels';
  return written === 0 ? 'No levels yet' : `${written} of 4 levels`;
};

const ImportCapabilitiesDialog: React.FC<{
  open: boolean;
  /** The role the capabilities are imported to. */
  role: SetupRole;
  /** Every role there is: only the other roles of this role's team are offered. */
  roles: SetupRole[];
  /** The name of the role's team. */
  teamName: string;
  onClose: () => void;
  onImport: (choices: ImportChoice[]) => void;
}> = ({ open, role, roles, teamName, onClose, onImport }) => {
  // What is picked, by the source capability's id.
  const [picked, setPicked] = useState<Record<string, SetupCapability>>({});
  const sources = useMemo(() => importable(roles, role), [roles, role]);

  const chosen = Object.values(picked);
  const room = MAX_CAPABILITIES - role.capabilities.length;
  const overRoom = chosen.length > room;
  const ready = chosen.length > 0 && !overRoom;

  const toggle = (capability: SetupCapability) =>
    setPicked((prev) => {
      if (capability.id in prev) {
        const { [capability.id]: dropped, ...rest } = prev;
        return rest;
      }
      return { ...prev, [capability.id]: capability };
    });
  const close = () => {
    setPicked({});
    onClose();
  };
  const confirm = () => {
    onImport(chosen.map((c) => ({ id: c.id, name: c.name, criteria: c.criteria })));
    close();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 640,
          maxWidth: '94vw',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#1C1C1C',
          backgroundImage: 'none',
          border: `1px solid ${LINE}`,
          borderRadius: '8px'
        }
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          p: '16px 20px',
          borderBottom: `1px solid ${LINE}`
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...SECTION_TITLE, whiteSpace: 'normal' }}>Import a capability from another role</Typography>
          <Typography sx={{ ...BODY, fontSize: 12.5, mt: '4px' }}>
            Choose capabilities from other roles in {teamName} to share with {role.name}.
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={close} sx={{ color: TEXT_2, '&:hover': { color: TEXT } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '14px 20px 16px', scrollbarWidth: 'thin' }}>
        <Note>
          A shared capability has the same name, levels and scoring for every role that uses it, so one program can
          build it for all of those roles. If you later edit the levels for one role, that role gets its own copy under
          a new name. Capabilities can be shared within a team only.
        </Note>
        {sources.length === 0 && (
          <Typography sx={{ ...BODY, color: TEXT_3, pt: '12px' }}>
            No other role in {teamName} has capabilities to import yet.
          </Typography>
        )}
        {sources.map((source) => (
          <Box key={source.role.id} sx={{ pt: '14px' }}>
            <Typography sx={{ ...LABEL, pb: '6px', color: TEXT, borderBottom: '1px solid rgba(247, 148, 32, 0.45)' }}>
              {source.role.name}
            </Typography>
            {source.capabilities.map(({ capability, held }) => {
              const on = capability.id in picked;
              // The same capability listed under another role that shares it, already picked there.
              const pickedElsewhere =
                !on && chosen.some((c) => c.id === capability.id || sameName(c.name, capability.name));
              const off = held || pickedElsewhere;
              return (
                <Box
                  key={capability.id}
                  component="label"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    minHeight: 40,
                    borderBottom: `1px solid ${LINE}`,
                    cursor: off ? 'default' : 'pointer',
                    opacity: off ? 0.55 : 1
                  }}
                >
                  <Checkbox
                    checked={on || pickedElsewhere}
                    disabled={off}
                    onChange={() => toggle(capability)}
                    size="small"
                    sx={{ p: '4px', color: '#565656', '&.Mui-checked': { color: BRANDY } }}
                  />
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: FONT,
                      fontSize: 13.5,
                      color: TEXT,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {capability.name}
                  </Typography>
                  <Typography
                    sx={{
                      ...LABEL,
                      textTransform: 'none',
                      letterSpacing: 0,
                      fontSize: 11,
                      flexShrink: 0,
                      color: TEXT_3
                    }}
                  >
                    {held ? `Already on ${role.name}` : levelsText(capability)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
          p: '12px 20px',
          borderTop: `1px solid ${LINE}`
        }}
      >
        {overRoom && (
          <Typography sx={{ flex: 1, fontFamily: FONT, fontSize: 12, color: DANGER }}>
            {role.name} has room for {Math.max(0, room)} more (at most {MAX_CAPABILITIES} capabilities).
          </Typography>
        )}
        <ActionButton onClick={close}>Cancel</ActionButton>
        <ActionButton
          primary
          disabled={!ready}
          reason={chosen.length === 0 ? 'Pick at least one capability.' : 'That is more than the role has room for.'}
          onClick={confirm}
        >
          {chosen.length > 1 ? `Import ${chosen.length} Capabilities` : 'Import Capability'}
        </ActionButton>
      </Box>
    </Dialog>
  );
};

export default ImportCapabilitiesDialog;
